import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import LegalParagraph from '@/app/components/LegalComponents/LegalParagraph';

export interface TermsAndConditionsSectionProps {
  readonly header: MessageKey;
  readonly lastUpdated?: MessageKey;
  readonly localePath: string;
  readonly paragraphs: (
    | MessageKey
    | {
        messageKey: MessageKey;
        className?: string;
        getLink?: (locale: string) => string;
      }
  )[];
  readonly showPreviousTermsLink?: boolean;
  readonly useH1Element?: boolean;
  readonly useH1Variant?: boolean;
}

const TermsAndConditionsSection = ({
  header,
  lastUpdated,
  localePath,
  paragraphs,
  showPreviousTermsLink = false,
  useH1Element = false,
  useH1Variant = false,
}: TermsAndConditionsSectionProps) => {
  const t = useTranslations();

  const headerElement = useH1Element ? 'h1' : 'h2';
  const headerVariant = useH1Variant ? 'h1' : 'h3';

  return (
    <section
      className={cx('space-y-4', {
        'mb-10': paragraphs.length > 0,
        'mb-8': paragraphs.length === 0,
      })}
      data-cy="terms-and-conditions-section"
    >
      <Typography
        Element={headerElement}
        variant={headerVariant}
        data-cy="terms-and-conditions-header"
      >
        {t(header)}
      </Typography>
      {showPreviousTermsLink && (
        <div className="mb-8">
          <BodyLink href={`${localePath}previous-terms-and-conditions`}>
            {t('previous_terms_and_conditions')}
          </BodyLink>
        </div>
      )}
      {lastUpdated && (
        <Typography data-cy="terms-and-conditions-last-updated">
          <em>{t(lastUpdated)}</em>
        </Typography>
      )}
      {paragraphs.map((paragraph, index) => {
        return typeof paragraph === 'string' ? (
          <LegalParagraph key={index} tx={paragraph} />
        ) : (
          <LegalParagraph
            className={paragraph.className}
            key={index}
            tx={paragraph.messageKey}
            values={
              paragraph.getLink && {
                link: (chunks) => (
                  <BodyLink
                    external
                    href={paragraph.getLink!(localePath)}
                    target="_blank"
                  >
                    {chunks}
                  </BodyLink>
                ),
              }
            }
          />
        );
      })}
    </section>
  );
};

export default TermsAndConditionsSection;
